import { useEffect, useMemo, useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { api, formatApiError } from "@/lib/api";
import { eur } from "@/lib/format";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { CourseFilters, MODALITY_LABELS, filterCourses, filterLabel, downloadCoursesPdf } from "@/components/app/CourseFilters";
import { toast } from "sonner";
import { UserPlus, Lock, Send, Loader2, CheckCircle2, Eye, FileDown } from "lucide-react";

const CONFIRM_MSG = "La tua richiesta di attivazione è stata inviata con successo. Sarà valutata dal nostro staff e verrai avvisato in caso di attivazione del corso.";

function RowActions({ c, busy, onView, onEnroll, onRequest }) {
  return (
    <div className="flex items-center justify-end gap-1 whitespace-nowrap">
      <Button variant="ghost" size="icon" title="Scheda corso" onClick={() => onView(c)} data-testid={`cliente-course-detail-${c.course_id}`}><Eye className="w-4 h-4" /></Button>
      <Button size="sm" disabled={!c.available} onClick={() => onEnroll(c)} className="bg-amber-500 hover:bg-amber-600 text-white" data-testid={`cliente-course-enroll-${c.course_id}`}>
        <UserPlus className="w-4 h-4 mr-1" /> Iscrivi
      </Button>
      {!c.available && (c.request_status ? (
        <Badge className="bg-sky-100 text-sky-800 hover:bg-sky-100 h-9 px-3" data-testid={`cliente-course-request-sent-${c.course_id}`}><CheckCircle2 className="w-4 h-4 mr-1" /> Richiesta inviata</Badge>
      ) : (
        <Button size="sm" variant="outline" disabled={busy} onClick={() => onRequest(c)} className="border-[#0F2942] text-[#0F2942]" data-testid={`cliente-course-request-${c.course_id}`}>
          {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />} Richiedi attivazione
        </Button>
      ))}
    </div>
  );
}

function CourseRow(props) {
  const { c } = props;
  const off = !c.available;
  return (
    <TableRow className={off ? "bg-slate-50" : ""} data-testid={`cliente-course-card-${c.course_id}`} data-available={c.available ? "1" : "0"}>
      <TableCell className={`font-medium ${off ? "text-slate-500" : ""}`}>
        <button type="button" onClick={() => props.onView(c)} className="text-left hover:text-amber-600" data-testid={`cliente-course-title-${c.course_id}`}>{c.title}</button>
        {off && <span className="inline-flex items-center gap-1 mt-1 rounded-full bg-red-600 text-white text-[11px] font-bold px-2 py-0.5" data-testid={`cliente-course-inactive-badge-${c.course_id}`}><Lock className="w-3 h-3" /> Corso non ancora attivato</span>}
      </TableCell>
      <TableCell className={off ? "text-slate-400" : ""}>{c.category}</TableCell>
      <TableCell className={off ? "text-slate-400" : ""}>{MODALITY_LABELS[c.modality] || c.modality}</TableCell>
      <TableCell className={off ? "text-slate-400" : ""}>{c.duration_hours ? `${c.duration_hours}h` : "—"}</TableCell>
      <TableCell className="text-slate-500 whitespace-nowrap" data-testid={`cliente-course-public-price-${c.course_id}`}>{c.public_price ? eur(c.public_price) : "—"}</TableCell>
      <TableCell className="font-semibold text-[#0F2942] whitespace-nowrap" data-testid={`cliente-course-price-${c.course_id}`}>{c.available && typeof c.price === "number" ? eur(c.price) : "—"}</TableCell>
      <TableCell className={off ? "text-slate-400" : ""}>{c.available ? `${c.vat_rate ?? 22}%` : "—"}</TableCell>
      <TableCell><RowActions {...props} /></TableCell>
    </TableRow>
  );
}

export default function ClienteCatalog() {
  const { user } = useAuth();
  if (user?.client?.billing_mode === "slot") return <Navigate to="/cliente" replace />;
  return <CatalogView />;
}

function CatalogView() {
  const navigate = useNavigate();
  const [courses, setCourses] = useState(null);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("all");
  const [mod, setMod] = useState("all");
  const [busyId, setBusyId] = useState(null);
  const [confirm, setConfirm] = useState(false);
  const [exporting, setExporting] = useState(false);

  useEffect(() => { api.get("/cliente/catalog/all").then((r) => setCourses(r.data)); }, []);
  const cats = useMemo(() => [...new Set((courses || []).map((c) => c.category).filter(Boolean))].sort(), [courses]);
  const shown = useMemo(() => filterCourses(courses || [], q, cat, mod).sort((a, b) => Number(b.available) - Number(a.available)), [courses, q, cat, mod]);
  const activeShown = shown.filter((c) => c.available);

  const request = async (c) => {
    setBusyId(c.course_id);
    try {
      await api.post("/cliente/activation-requests", { course_id: c.course_id });
      setCourses((list) => list.map((x) => (x.course_id === c.course_id ? { ...x, request_status: "nuova" } : x)));
      setConfirm(true);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusyId(null); }
  };
  const exportPdf = async () => {
    setExporting(true);
    try { await downloadCoursesPdf("/cliente/export/catalog.pdf", activeShown.map((c) => c.course_id), filterLabel(q, cat, mod)); }
    catch { toast.error("Esportazione PDF non riuscita"); }
    finally { setExporting(false); }
  };

  if (!courses) return <div className="text-slate-500">Caricamento…</div>;
  return (
    <div className="space-y-6" data-testid="cliente-catalog-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-display text-3xl font-extrabold text-slate-900">Catalogo corsi</h1>
          <p className="text-slate-500 mt-1">I corsi attivi sono subito disponibili per l'iscrizione. Per gli altri puoi richiedere l'attivazione.</p>
        </div>
        <Button variant="outline" onClick={exportPdf} disabled={exporting || activeShown.length === 0} data-testid="cliente-catalog-export-pdf">
          {exporting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <FileDown className="w-4 h-4 mr-1" />} Esporta PDF
        </Button>
      </div>
      <CourseFilters q={q} setQ={setQ} cat={cat} setCat={setCat} mod={mod} setMod={setMod} categories={cats} count={shown.length} />
      <Card className="border-slate-200"><CardContent className="p-0 overflow-x-auto">
        <Table className="min-w-[900px]">
          <TableHeader><TableRow>
            <TableHead>Titolo</TableHead><TableHead>Categoria</TableHead><TableHead>Modalità</TableHead>
            <TableHead>Durata</TableHead><TableHead>Prezzo pubblico</TableHead><TableHead>Prezzo riservato</TableHead><TableHead>IVA</TableHead><TableHead className="text-right">Azioni</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {shown.map((c) => (
              <CourseRow key={c.course_id} c={c} busy={busyId === c.course_id} onRequest={request} onView={(x) => navigate(`/cliente/corsi/${x.course_id}`)}
                onEnroll={(x) => navigate(`/cliente/iscrizioni?corso=${x.course_id}`)} />
            ))}
            {shown.length === 0 && <TableRow><TableCell colSpan={8} className="text-center text-slate-500 py-12" data-testid="cliente-catalog-empty">Nessun corso trovato.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </CardContent></Card>
      <Dialog open={confirm} onOpenChange={setConfirm}>
        <DialogContent data-testid="activation-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><CheckCircle2 className="w-5 h-5 text-emerald-600" /> Richiesta inviata</DialogTitle>
            <DialogDescription data-testid="activation-confirm-message">{CONFIRM_MSG}</DialogDescription>
          </DialogHeader>
          <DialogFooter><Button onClick={() => setConfirm(false)} className="bg-[#0F2942] hover:bg-[#1B3B5F]" data-testid="activation-confirm-close">Chiudi</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
